import { router } from '@/plugins/router'
import { Routes } from '@/plugins/router/constants'
import { settingsService } from '@/services/settingsService'
import { useErrorStore } from '@/stores/errorStore'
import { useIdentityStore } from '@/stores/identityStore'

export function useRedirectUrlHandler() {
  const redirect = async () => {
    try {
      const identityStore = useIdentityStore()
      const currentUser = await identityStore.getCurrentUser()

      if (!currentUser.isAuthenticated) {
        await router.push(Routes.Login)
      } else {
        const redirectUrl = isValidRedirectUrl(identityStore.input.redirectUrl)
          ? identityStore.input.redirectUrl
          : settingsService.defaultRedirectUrl

        identityStore.clearRedirectUrl()
        window.location.href = redirectUrl
      }
    } catch (error: any) {
      const errorStore = useErrorStore()

      if (error?.message?.includes('Access token is not available')) {
        const message = 'Authentication failed. Please sign in again.'
        errorStore.addError(message)
        console.error(
          'Access token not available after retries. This may indicate an MSAL cache or redirect issue.',
        )
      } else {
        const message = error?.message || 'Failed to redirect. Please try again.'
        errorStore.addError(message)
        console.error('Error in redirect handler:', error)
      }
      await router.push(Routes.Login)
    }
  }

  return {
    redirect,
    isValidRedirectUrl,
  }
}

/** Single-label org host: https://{org}.{VITE_ORBVIZ_DOMAIN} (same rule as the former regex). */
function originMatchesOrbvizSubdomainPattern(origin: string): boolean {
  try {
    const u = new URL(origin)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false
    const host = u.hostname.toLowerCase()
    const base = settingsService.orbvizDomain.toLowerCase()
    const suffix = `.${base}`
    if (!host.endsWith(suffix)) return false
    const orgLabel = host.slice(0, -suffix.length)
    return orgLabel.length > 0 && !orgLabel.includes('.')
  } catch {
    return false
  }
}

export function isValidRedirectUrl(url: string) {
  if (url && settingsService.isDevelopment) return true

  if (!url) return false

  let origin: string
  try {
    origin = new URL(url).origin
  } catch {
    return false
  }

  const allowed = settingsService.allowedRedirectOrigins
  if (allowed.includes(origin)) {
    return true
  }

  return originMatchesOrbvizSubdomainPattern(origin)
}
